import {
  createContext,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useSession } from '~/hooks'
import { Braze } from '~/lib'
import { BrazeProviderProps } from './BrazeProvider.types'

export const BrazeProviderContext = createContext<BrazeProviderProps>({})

export const BrazeProvider = ({ children }: PropsWithChildren) => {
  const [isInitialized, setIsInitialized] = useState(false)
  const { session } = useSession()
  const [isSessionInitialized, setIsSessionInitialized] = useState(false)
  const [isBannerModuleInitialized, setIsBannerModuleInitialized] =
    useState(false)
  const bannersRef = useRef<object>({})
  const [bannersHash, setBannersHash] = useState<string>('')
  const requestedBannerKeysRef = useRef<Record<string, boolean>>({})

  const requestBannersRefresh = useCallback(
    (key: string, bannerPlacementIds: string[]) => {
      if (requestedBannerKeysRef.current[key] || !bannerPlacementIds?.length) {
        return
      }

      requestedBannerKeysRef.current[key] = true

      window.braze?.requestBannersRefresh(bannerPlacementIds)
    },
    []
  )

  useEffect(() => {
    const interval = setInterval(() => {
      if (Braze.isInitialized()) {
        setIsInitialized(true)
        clearInterval(interval)
      }
    }, 250)

    return () => {
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    if (!isInitialized) {
      return
    }

    setIsSessionInitialized(false)
    Braze.setDeviceIdAlias()
    Braze.pushDataUntilSuccessful().then((isSuccessful) => {
      setIsSessionInitialized(isSuccessful)
    })
  }, [isInitialized, session?.token?.customerId])

  useEffect(() => {
    if (!isSessionInitialized) {
      return
    }

    const subscriptionId = Braze.subscribeToBannerUpdates((freshBanners) => {
      let tmpHash = ''

      for (const placementId in freshBanners) {
        const banner = freshBanners[placementId]

        tmpHash += banner.id
      }

      setBannersHash(tmpHash)
      bannersRef.current = freshBanners
      setIsBannerModuleInitialized(true)
    })

    Braze.refreshBanners()
      .then()
      .catch(() => {})

    return () => {
      Braze.removeSubscription(subscriptionId)
    }
  }, [isSessionInitialized])

  const value = useMemo(() => {
    return {
      isInitialized,
      isSessionInitialized,
      isBannerModuleInitialized,
      banners: bannersRef.current,
      bannersHash,
      requestBannersRefresh,
    }
  }, [
    isBannerModuleInitialized,
    isInitialized,
    isSessionInitialized,
    bannersHash,
    requestBannersRefresh,
  ])

  return (
    <BrazeProviderContext.Provider value={value}>
      {children}
    </BrazeProviderContext.Provider>
  )
}
